import React, { useContext, useMemo } from 'react';
import { useClient } from './useClient';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { GetUserPreferencesResponse } from '@hud/portal-dtos';
import { useDebouncedCallback } from '@mantine/hooks';
import {
  NotificationType,
  showNotification,
} from '../components/ToastNotification/ToastNotification';
import { PORTAL_BASE } from '../consts';

const USER_PREFERENCES_QUERY_KEY = 'UserPreferences';

type UserPreferencesContextProps = {
  userPreferredServices?: number[] | null;
  showExtensionBanner: boolean;
  updateUserPreferences: (userPreferences: UserPreferencesUpdate) => void;
};

const UserPreferencesContext = React.createContext<
  UserPreferencesContextProps | undefined
>(undefined);

export function UserPreferencesProvider({ children }: { children: React.ReactNode }) {
  const { client, isReady } = useClient();

  const { debouncedUpdateUserPrefs: updateUserPreferences } = useUpdateUserPreferences();

  const query = useQuery({
    queryKey: [USER_PREFERENCES_QUERY_KEY],
    queryFn: async () => {
      const response = await client.get<GetUserPreferencesResponse>(
        `${PORTAL_BASE}/user-preferences`,
      );
      return GetUserPreferencesResponse.parse(response.data);
    },
    enabled: isReady,
    // keep the previous data while reloading data
    placeholderData: keepPreviousData,
    initialData: {
      selectedServices: [],
      showExtensionBanner: false,
    },
  });

  const memoizedContext = useMemo(() => {
    return {
      userPreferredServices: query.data?.selectedServices || [],
      showExtensionBanner: query.data?.showExtensionBanner || false,
      updateUserPreferences,
    };
  }, [query.data?.selectedServices, query.data?.showExtensionBanner]);

  if (query.isLoading) {
    return <div></div>;
  }
  return (
    <UserPreferencesContext.Provider value={memoizedContext}>
      {children}
    </UserPreferencesContext.Provider>
  );
}

export function useUserPreferences() {
  const context = useContext(UserPreferencesContext);
  if (context === undefined) {
    throw new Error('useUserPreferences must be used within a UserPreferencesProvider');
  }

  return context;
}

type UserPreferencesUpdate = {
  selectedServices?: number[] | null;
  showExtensionBanner?: boolean;
};

function useUpdateUserPreferences() {
  const { client } = useClient();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (userPreferences: UserPreferencesUpdate) => {
      const currentData = queryClient.getQueryData<GetUserPreferencesResponse>([
        USER_PREFERENCES_QUERY_KEY,
      ]);
      await client.put(`${PORTAL_BASE}/user-preferences`, {
        selectedServices:
          userPreferences.selectedServices !== undefined
            ? userPreferences.selectedServices
            : (currentData?.selectedServices ?? []),
        showExtensionBanner: userPreferences.showExtensionBanner,
      });
    },
    onMutate: async (userPreferences: UserPreferencesUpdate) => {
      await queryClient.cancelQueries({ queryKey: [USER_PREFERENCES_QUERY_KEY] });

      const previousData = queryClient.getQueryData([USER_PREFERENCES_QUERY_KEY]);

      queryClient.setQueryData(
        [USER_PREFERENCES_QUERY_KEY],
        (old: GetUserPreferencesResponse): GetUserPreferencesResponse => ({
          ...old,
          ...(userPreferences.selectedServices !== undefined && {
            selectedServices: userPreferences.selectedServices,
          }),
          ...(userPreferences.showExtensionBanner !== undefined && {
            showExtensionBanner: userPreferences.showExtensionBanner,
          }),
        }),
      );

      return { previousData };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [USER_PREFERENCES_QUERY_KEY] });
    },
    onError: (err, newUserPreferences, context) => {
      if (context?.previousData) {
        queryClient.setQueryData([USER_PREFERENCES_QUERY_KEY], context.previousData);
      }
      showNotification({
        type: NotificationType.ERROR,
        message: 'There was an error updating your preferences, please try again.',
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [USER_PREFERENCES_QUERY_KEY] });
    },
  });

  const debouncedUpdateUserPrefs = useDebouncedCallback(mutation.mutate, 500);

  return {
    ...mutation,
    debouncedUpdateUserPrefs,
  };
}
